import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { ConsoleShell, PageHead } from "../components/ConsoleShell.js";
import { Card } from "../components/Card.js";
import { Tag, Btn } from "../components/index.js";
import { LoadingState, ErrorState, EmptyState } from "../components/EmptyState.js";
import { Icon } from "../components/Icon.js";
import { useAsync } from "../lib/useAsync.js";
import { manuals, type ReviewResult, type ReviewIssue } from "../lib/api.js";

const SEV_META: Record<ReviewIssue["severity"], { cls: string; label: string; dot: string }> = {
  high: { cls: "s-hi", label: "安全", dot: "var(--danger)" },
  medium: { cls: "s-md", label: "要改善", dot: "var(--warn)" },
  low: { cls: "s-lo", label: "提案", dot: "var(--info)" },
};

/** Derived headline from the score. */
function verdict(score: number): string {
  if (score >= 90) return "公開可能 — 軽微な指摘のみ";
  if (score >= 70) return "要確認 — いくつかの改善点";
  return "公開前に修正が必要";
}

export function AiReview() {
  const [params] = useSearchParams();
  // Target manual comes from ?id= (set when arriving from Preview / Library).
  const targetId = params.get("id") ?? "";

  const { data, loading, error, offline, reload } = useAsync<ReviewResult>(
    () => manuals.review(targetId),
    [targetId],
  );

  const counts = useMemo(() => {
    const c = { high: 0, medium: 0, low: 0 };
    (data?.issues ?? []).forEach((it) => {
      c[it.severity] += 1;
    });
    return c;
  }, [data]);

  const score = data?.score ?? 0;

  return (
    <ConsoleShell
      breadcrumb={["TEBIKI", "ライブラリ", "AIレビュー"]}
      actions={
        <>
          <Btn onClick={reload} disabled={loading}>
            {loading ? "レビュー中…" : "再レビュー"}
          </Btn>
          <Btn variant="primary" disabled title="準備中" aria-disabled="true">
            提案を一括適用
          </Btn>
        </>
      }
    >
      <PageHead title={<>AI<span className="hl">品質ゲート</span></>}>
        公開前に、手順の抜け・曖昧さ・安全リスクを自動検査。業種別チェックリスト（飲食・小売）を適用中。
      </PageHead>

      {!targetId ? (
        <EmptyState icon={<Icon name="ai-review" />} title="レビューするマニュアルがありません">
          ライブラリやプレビューからマニュアルを開き、「AIレビュー」を実行してください。
        </EmptyState>
      ) : loading ? (
        <LoadingState label="AIが品質を検査中…" />
      ) : error ? (
        <ErrorState message={error} offline={offline} onRetry={reload} />
      ) : data ? (
        <>
          <Card
            crop
            className="score reveal"
            style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))", gap: 22, alignItems: "center", padding: 22, marginBottom: 20 }}
          >
            <div
              className="ring"
              role="img"
              aria-label={`品質スコア ${score} / 100`}
              style={{
                width: 108,
                height: 108,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                flex: "0 0 auto",
                background: `conic-gradient(var(--hi-deep) 0 ${score}%,var(--line) ${score}% 100%)`,
              }}
            >
              <div
                className="inner"
                style={{ width: 84, height: 84, borderRadius: "50%", background: "var(--surface)", display: "grid", placeItems: "center", fontFamily: "var(--font-display)", fontSize: 32 }}
              >
                {score}
              </div>
            </div>
            <div>
              <div className="kicker">QUALITY SCORE</div>
              <div className="display" style={{ fontSize: 24, margin: "4px 0" }}>
                {verdict(score)}
              </div>
              <div className="row" style={{ flexWrap: "wrap" }}>
                <Tag dot color="var(--danger)">安全 {counts.high}</Tag>
                <Tag dot color="var(--warn)">要改善 {counts.medium}</Tag>
                <Tag dot color="var(--info)">提案 {counts.low}</Tag>
              </div>
            </div>
          </Card>

          {data.issues.length ? (
            <div className="list reveal d1">
              {data.issues.map((it, i) => {
                const meta = SEV_META[it.severity];
                return (
                  <div
                    key={i}
                    className="issue"
                    style={{ display: "grid", gridTemplateColumns: "auto 1fr auto", gap: 14, alignItems: "start", padding: "16px 18px", borderBottom: i < data.issues.length - 1 ? "1px solid var(--line)" : undefined }}
                  >
                    <span className={`sev ${meta.cls}`}>{meta.label}</span>
                    <div>
                      <b className="jp">
                        {it.stepSeq != null ? `手順${it.stepSeq}: ` : ""}
                        {it.title}
                      </b>
                      {it.suggestion ? (
                        <p className="jp" style={{ margin: "4px 0 0", color: "var(--muted)", fontSize: 14 }}>
                          {it.suggestion}
                        </p>
                      ) : null}
                    </div>
                    <button className="btn" style={{ minHeight: 38, padding: "8px 14px" }} disabled title="準備中" aria-disabled="true">
                      適用
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <EmptyState icon={<Icon name="approval" />} title="指摘はありません">
              この内容はそのまま公開できます。
            </EmptyState>
          )}
        </>
      ) : null}
    </ConsoleShell>
  );
}
